import { useEffect, useMemo, useState } from "react";
import { Copy, Download, Save, Pencil, Eye, RefreshCw, Loader2, Sparkles, AlertTriangle, FileText, FileType2, FileDown, ChevronDown, Calendar, CalendarDays, MapPin, UserCircle, PenLine, MessageSquareQuote, Palette, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Calendar as CalendarPicker } from "./ui/calendar";

const stripMarkers = (t) => (t || "").replace(/[⟦⟧]/g, "");

const NL_MONTHS = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const pad2 = (n) => String(n).padStart(2, "0");
const isoFromDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const dutchFromDate = (d) => `${d.getDate()} ${NL_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
const dateFromIso = (iso) => {
  const m = (iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
};

export function TranscriptionPane({ result, loading, liveText, meta, setMeta, onAddHint, onLearnCorrection, onRetranscribe, onSave, onTextChange, autoSaved, saving, hasImages, onExport }) {
  const [editMode, setEditMode] = useState(false);
  const [text, setText] = useState("");

  useEffect(() => {
    if (result) setText(result.transcription || "");
  }, [result]);

  const plainText = useMemo(() => stripMarkers(text), [text]);

  useEffect(() => {
    onTextChange?.(plainText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plainText]);

  const editFlag = (occIndex, value) => {
    let count = -1;
    setText((t) => t.replace(/⟦([^⟧]*)⟧/g, (m) => (++count === occIndex ? value : m)));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      toast.success("Gekopieerd naar klembord");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = plainText;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Gekopieerd naar klembord");
      } catch {
        toast.error("Kopiëren niet gelukt");
      }
      document.body.removeChild(ta);
    }
  };
  const download = () => {
    const blob = new Blob([plainText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transcriptie.txt";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Gedownload als .txt");
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--surface)] px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-3)]">Transcriptie</span>
          {result && (
            meta?.is_typed ? (
              <span data-testid="typed-badge" title="De AI herkende dit als getypte (machinaal getikte) tekst" className="flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-2)]">
                <Keyboard className="h-3 w-3 text-[var(--brand)]" /> Getypt
              </span>
            ) : (
              <span data-testid="handwritten-badge" title="De AI herkende dit als handgeschreven tekst" className="flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-2)]">
                <PenLine className="h-3 w-3 text-[var(--brand)]" /> Handgeschreven
              </span>
            )
          )}
        </div>
        {result && (
          <div className="flex items-center gap-1">
            <HeadBtn onClick={() => setEditMode((e) => !e)} testid="toggle-edit" active={editMode}>
              {editMode ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              <span className="hidden sm:inline">{editMode ? "Weergave" : "Bewerken"}</span>
            </HeadBtn>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-5 sm:p-6">
        {loading ? (
          liveText ? (
            <div className="animate-fade-up" data-testid="transcription-live">
              <div className="mb-3 flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-[var(--brand)]">
                <Sparkles className="h-3.5 w-3.5 animate-pulse" /> Live meeschrijven…
              </div>
              <div className="whitespace-pre-wrap font-serif text-[16px] leading-8 text-[var(--ink)]">
                <HighlightedText text={liveText} />
                <span className="ml-0.5 inline-block h-5 w-[2px] translate-y-1 animate-pulse bg-[var(--brand)]" />
              </div>
            </div>
          ) : (
            <LoadingState />
          )
        ) : !result ? (
          <EmptyState hasImages={hasImages} />
        ) : (
          <div className="animate-fade-up space-y-5">
            <MetaCard meta={meta} setMeta={setMeta} />
            <SummaryCard meta={meta} setMeta={setMeta} />
            <ToneCard meta={meta} setMeta={setMeta} />
            {!editMode && text.includes("⟦") && (
              <div className="flex items-start gap-2 rounded-lg border border-[var(--amber-border)]/60 bg-[var(--amber-bg)] px-3 py-2 text-xs text-[var(--amber-text)]" data-testid="flag-legend">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>De <span className="flagged">gekleurde</span> woorden kon de AI niet zeker lezen en zijn op basis van context ingevuld. Klik erop om te corrigeren — de kleur verdwijnt zodra je het aanpast.</span>
              </div>
            )}
            {editMode ? (
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="transcription-textarea"
                className="min-h-[320px] w-full resize-y rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-4 font-serif text-[15px] leading-8 text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
              />
            ) : (
              <div
                data-testid="transcription-output"
                className="whitespace-pre-wrap font-serif text-[16px] leading-8 text-[var(--ink)]"
              >
                <HighlightedText text={text} editable onEditFlag={editFlag} onLearnCorrection={onLearnCorrection} />
              </div>
            )}

            {result.uncertain?.length > 0 && !editMode && (
              <div className="rounded-xl border border-[var(--amber-border)] bg-[var(--amber-bg)] p-4">
                <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-[var(--amber-text)]">
                  <AlertTriangle className="h-4 w-4" /> Onduidelijke passages ({result.uncertain.length})
                </div>
                <p className="mb-3 text-xs text-[var(--amber-text)] opacity-80">
                  Help de AI leren: geef aan wat er écht staat en transcribeer opnieuw.
                </p>
                <ul className="space-y-2">
                  {result.uncertain.map((u, i) => (
                    <UncertainItem key={i} item={u} index={i} onAddHint={onAddHint} />
                  ))}
                </ul>
              </div>
            )}

            {result.notes && !editMode && (
              <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3 text-sm text-[var(--ink-2)]">
                <span className="font-semibold text-[var(--ink)]">Opmerking AI: </span>
                {result.notes}
              </div>
            )}
          </div>
        )}
      </div>

      {result && (
        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border-subtle)] bg-[var(--surface)] px-4 py-3">
          <ActBtn onClick={copy} testid="copy-txt-button"><Copy className="h-4 w-4" /> Kopiëren</ActBtn>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="export-button"
                className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm font-medium text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"
              >
                <FileDown className="h-4 w-4" /> Exporteren <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-[var(--surface)]">
              <DropdownMenuItem onClick={download} data-testid="download-txt-button" className="gap-2 text-[var(--ink)]">
                <Download className="h-4 w-4" /> Platte tekst (.txt)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport("docx", plainText)} data-testid="export-docx-button" className="gap-2 text-[var(--ink)]">
                <FileType2 className="h-4 w-4" /> Word-document (.docx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport("pdf", plainText)} data-testid="export-pdf-button" className="gap-2 text-[var(--ink)]">
                <FileText className="h-4 w-4" /> PDF-document (.pdf)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ActBtn onClick={onRetranscribe} testid="retranscribe-button"><RefreshCw className="h-4 w-4" /> Opnieuw</ActBtn>
          <div className="flex-1" />
          {autoSaved && (
            <span data-testid="autosave-indicator" className="flex items-center gap-1 text-xs text-[var(--ink-3)]">
              <Save className="h-3.5 w-3.5 text-green-600" /> Automatisch opgeslagen
            </span>
          )}
          <button
            onClick={() => onSave(plainText)}
            disabled={saving}
            data-testid="save-letter-button"
            className="flex items-center gap-2 rounded-lg bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--brand-hover)] disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {autoSaved ? "Opgeslagen" : "Opslaan"}
          </button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ meta, setMeta }) {
  if (!meta) return null;
  return (
    <div className="rounded-xl border border-[var(--brand)]/25 bg-[var(--brand)]/5 p-3" data-testid="summary-card">
      <span className="mb-1.5 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--brand)]">
        <Sparkles className="h-3.5 w-3.5" /> Samenvatting
      </span>
      <textarea
        value={meta.summary || ""}
        onChange={(e) => setMeta({ ...meta, summary: e.target.value })}
        placeholder="Korte samenvatting van waar de brief over gaat…"
        data-testid="meta-summary-input"
        className="min-h-[52px] w-full resize-y rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] p-2.5 text-sm italic leading-relaxed text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
      />
    </div>
  );
}

function ToneCard({ meta, setMeta }) {
  if (!meta) return null;
  return (
    <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3" data-testid="tone-card">
      <span className="mb-1.5 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
        <Palette className="h-3.5 w-3.5" /> Toon / sfeer
      </span>
      <input
        value={meta.tone || ""}
        onChange={(e) => setMeta({ ...meta, tone: e.target.value })}
        placeholder="bijv. hartelijk, persoonlijk, respectvol…"
        data-testid="meta-tone-input"
        className="h-9 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
      />
    </div>
  );
}

function MetaCard({ meta, setMeta }) {
  const [calOpen, setCalOpen] = useState(false);
  if (!meta) return null;
  const set = (k) => (e) => setMeta({ ...meta, [k]: e.target.value });
  const pickDate = (d) => {
    if (!d) return;
    setMeta({ ...meta, date_text: dutchFromDate(d), date_iso: isoFromDate(d) });
    setCalOpen(false);
  };
  const rest = [
    { k: "writer", label: "Schrijver", icon: UserCircle, ph: "wie schreef dit? bijv. Pieter, Ineke of een andere naam" },
    { k: "place", label: "Plaats", icon: MapPin, ph: "bijv. Arnhem" },
    { k: "salutation", label: "Aanhef", icon: MessageSquareQuote, ph: "bijv. Lieve moeder," },
    { k: "signature", label: "Ondertekening", icon: PenLine, ph: "bijv. Uw zoon, Hendrik" },
  ];
  return (
    <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3" data-testid="meta-card">
      <div className="mb-2.5 flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-3)]">Brief-gegevens</span>
        {meta.date_iso && (
          <span className="font-mono text-[11px] text-[var(--ink-3)]" title="Genormaliseerde datum voor sortering">⌁ {meta.date_iso}</span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {/* Datum met datumkiezer */}
        <label className="flex flex-col gap-1">
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
            <Calendar className="h-3 w-3" /> Datum
          </span>
          <div className="flex gap-1.5">
            <input
              value={meta.date_text || ""}
              onChange={(e) => setMeta({ ...meta, date_text: e.target.value })}
              placeholder="bijv. 14 januari 1918"
              data-testid="meta-date_text-input"
              className="h-9 min-w-0 flex-1 rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
            />
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="meta-date-picker-btn"
                  title="Datum kiezen"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)] hover:text-[var(--brand)]"
                >
                  <CalendarDays className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto bg-[var(--surface)] p-0" align="end" data-testid="meta-date-picker">
                <CalendarPicker
                  mode="single"
                  selected={dateFromIso(meta.date_iso)}
                  onSelect={pickDate}
                  defaultMonth={dateFromIso(meta.date_iso)}
                  captionLayout="dropdown-buttons"
                  fromYear={1700}
                  toYear={new Date().getFullYear()}
                  classNames={{ caption_label: "hidden", caption_dropdowns: "flex gap-2 justify-center" }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </label>

        {rest.map((f) => (          <label key={f.k} className="flex flex-col gap-1">
            <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
              <f.icon className="h-3 w-3" /> {f.label}
            </span>
            <input
              value={meta[f.k] || ""}
              onChange={set(f.k)}
              placeholder={f.ph}
              data-testid={`meta-${f.k}-input`}
              className="h-9 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
            />
          </label>
        ))}
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
            <UserCircle className="h-3 w-3" /> Relatie tot Pieter/Ineke <span className="normal-case text-[var(--ink-3)]">(voor andere schrijvers)</span>
          </span>
          <input
            value={meta.writer_relation || ""}
            onChange={set("writer_relation")}
            placeholder="bijv. zus van Ineke, buurman van Pieter, moeder van Pieter"
            data-testid="meta-writer_relation-input"
            className="h-9 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
          />
        </label>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
            <Calendar className="h-3 w-3" /> AI-opmerking bij datum
          </span>
          <input
            value={meta.date_note || ""}
            onChange={(e) => setMeta({ ...meta, date_note: e.target.value })}
            placeholder="bijv. datum bij benadering — ontbreekt in de brief / afgeleid van poststempel"
            data-testid="meta-date_note-input"
            className="h-9 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
          />
        </label>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
            <PenLine className="h-3 w-3" /> Mijn opmerking bij datum
          </span>
          <input
            value={meta.date_user_note || ""}
            onChange={(e) => setMeta({ ...meta, date_user_note: e.target.value })}
            placeholder="bijv. datum onduidelijk/ontbreekt — ik heb op basis van de context gegokt"
            data-testid="meta-date_user_note-input"
            className="h-9 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
          />
        </label>
      </div>
    </div>
  );
}

function HighlightedText({ text, editable, onEditFlag, onLearnCorrection }) {
  const parts = useMemo(() => {
    const out = [];
    const re = /⟦([^⟧]*)⟧/g;
    let last = 0;
    let m;
    while ((m = re.exec(text || "")) !== null) {
      if (m.index > last) out.push({ t: "plain", v: text.slice(last, m.index) });
      out.push({ t: "flag", v: m[1] });
      last = re.lastIndex;
    }
    if (last < (text || "").length) out.push({ t: "plain", v: text.slice(last) });
    return out;
  }, [text]);

  let flagCount = -1;
  return (
    <>
      {parts.map((p, i) => {
        if (p.t === "plain") return <PlainWithSlots key={i} text={p.v} />;
        flagCount += 1;
        const occ = flagCount;
        if (!editable) return <span key={i} className="flagged">{p.v || "??"}</span>;
        return <FlagEditable key={i} word={p.v} occIndex={occ} onEditFlag={onEditFlag} onLearnCorrection={onLearnCorrection} />;
      })}
    </>
  );
}

function PlainWithSlots({ text }) {
  const re = /(?<![0-9A-Za-zÀ-ÿ])x{2,}(?![0-9A-Za-zÀ-ÿ])/gi;
  const out = [];
  let last = 0, m, k = 0;
  while ((m = re.exec(text || "")) !== null) {
    if (m.index > last) out.push(<span key={`t${k}`}>{text.slice(last, m.index)}</span>);
    out.push(<span key={`u${k}`} className="unreadable" title="Onleesbaar woord — markeer met xx/xxx. In het Brievenboek kunnen lezers hier een suggestie doen.">{m[0]}</span>);
    last = re.lastIndex;
    k += 1;
  }
  if (last < (text || "").length) out.push(<span key={`t${k}`}>{text.slice(last)}</span>);
  return <>{out}</>;
}

function FlagEditable({ word, occIndex, onEditFlag, onLearnCorrection }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(word);
  useEffect(() => setVal(word), [word]);
  const commit = () => {
    onEditFlag(occIndex, val);
    onLearnCorrection?.(word, val);
    setEditing(false);
    toast.success("Woord aangepast");
  };
  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setVal(word); setEditing(false); }
        }}
        data-testid={`flag-edit-input-${occIndex}`}
        style={{ width: `${Math.max(2, val.length + 1)}ch` }}
        className="mx-0.5 rounded border border-[var(--brand)] bg-[var(--surface)] px-1 font-serif text-[16px] text-[var(--ink)] outline-none"
      />
    );
  }
  return (
    <span
      className="flagged"
      role="button"
      tabIndex={0}
      title="AI heeft dit op basis van context ingevuld — klik om te corrigeren"
      data-testid={`flagged-word-${occIndex}`}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => { if (e.key === "Enter") setEditing(true); }}
    >
      {word || "??"}
    </span>
  );
}

function UncertainItem({ item, index, onAddHint }) {
  const [val, setVal] = useState(item.guess || "");
  return (
    <li className="rounded-lg bg-[var(--surface)]/60 p-2.5" data-testid={`uncertain-item-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-serif text-sm font-semibold text-[var(--ink)]">"{item.snippet || item.guess}"</p>
          {item.reason && <p className="mt-0.5 text-xs text-[var(--ink-3)]">{item.reason}</p>}
        </div>
      </div>
      <div className="mt-2 flex gap-2">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Correcte tekst…"
          data-testid={`uncertain-input-${index}`}
          className="flex-1 rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 py-1.5 text-xs text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--amber-border)]"
        />
        <button
          onClick={() => {
            if (!val.trim()) return;
            onAddHint(`Het stuk dat gelezen werd als "${item.snippet || item.guess}" is eigenlijk: "${val.trim()}"`);
            toast.success("Leeshulp toegevoegd");
          }}
          data-testid={`uncertain-add-${index}`}
          className="rounded-md bg-[var(--amber-border)] px-3 text-xs font-semibold text-black hover:brightness-95"
        >
          Leren
        </button>
      </div>
    </li>
  );
}

function LoadingState() {
  return (
    <div className="flex h-full flex-col items-center justify-center py-16 text-center">
      <div className="relative">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--surface-muted)]">
          <Sparkles className="h-7 w-7 animate-pulse text-[var(--brand)]" />
        </div>
      </div>
      <p className="mt-5 font-display text-base font-semibold text-[var(--ink)]">Handschrift wordt gelezen…</p>
      <p className="mt-1 text-sm text-[var(--ink-3)]">De AI bestudeert het handschrift en de context</p>
    </div>
  );
}

function EmptyState({ hasImages }) {
  return (
    <div className="flex h-full flex-col items-center justify-center py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--surface-muted)] text-[var(--ink-3)]">
        <Sparkles className="h-7 w-7" />
      </div>
      <p className="mt-5 font-display text-base font-semibold text-[var(--ink)]">Nog geen transcriptie</p>
      <p className="mt-1 max-w-xs text-sm text-[var(--ink-3)]">
        {hasImages
          ? "Vul eventueel context en leeshulp in en klik op ‘Transcriberen’."
          : "Upload eerst een scan van een handgeschreven brief."}
      </p>
    </div>
  );
}

function HeadBtn({ children, onClick, active, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
        active ? "bg-[var(--brand)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
      }`}
    >
      {children}
    </button>
  );
}

function ActBtn({ children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm font-medium text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"
    >
      {children}
    </button>
  );
}
